<template>
    <div
        v-if="!creatingCampaign"
        :class="{'!mx-24 !my-0 py-24': creatingCampaign}"
    >
        <div>
            <div class="flex items-end justify-between">
                <campaign-filters
                   class="shrink-1" @filter-changes="getCampaigns"
                    @filter-archived="filterArchived"
                />
                <simple-button
                    class="shrink-0"
                    display="secondary"
                    icon="add-circle"
                    :loading="creatingCampaign"
                    look="primary"
                    @click="multiPostingStore.setCreatingCampaign(true)"
                >
                    {{ t('multi-posting.tabs.campaigns.add') }}
                </simple-button>
            </div>
            <div style="clear: both;"></div>
            <div class="border-t w-full my-24"></div>
            <div class="mb-24">
                <p class="text-heading-2 font-semibold">{{ $t('multi-posting.tabs.campaigns.title') }}</p>
            </div>
            <div v-if="fetching" class="flex justify-center items-center">
                <loading />
            </div>
            <div v-else>
                <empty-state
                    v-if="filteredCampaigns.length === 0"
                    :title="t('multi-posting.tabs.campaigns.empty.title')"
                    :description="t(hasFiltersApplied ? 'multi-posting.tabs.campaigns.empty.description' : 'multi-posting.tabs.campaigns.empty.no_campaigns')"
                    :showButton="true"
                    :button-text="hasFiltersApplied ? t('general.try_again') : t('multi-posting.tabs.campaigns.add')"
                    :button-color="hasFiltersApplied ? 'primary' : 'secondary'"
                    :button-icon="hasFiltersApplied ? 'replay' : 'add-circle'"
                    :show-animation="hasFiltersApplied"
                    @click="hasFiltersApplied ? getCampaigns(filters) : multiPostingStore.setCreatingCampaign(true)"
                />
                <div v-else class="space-y-12">
                    <template v-for="group in campaignGroups">
                        <div v-if="!group.hidden">
                            <div
                                class="flex items-center gap-8 cursor-pointer w-fit mb-4"
                                @click="toggleGroup(group.name)"
                            >
                                <div class="flex items-center gap-4">
                                    <p class="text-black font-semibold">{{ group.title }}</p>
                                    <badge :content="group.campaigns.length" class="w-fit"/>
                                </div>
                                <svg-icon
                                    type="keyboard-arrow-down"
                                    :class="{'rotate-180': group.open}"
                                    class="transition-transform"
                                />
                            </div>
                            <div v-if="group.open" class="py-8 space-y-8">
                                <div v-for="campaign in group.campaigns" :key="campaign.id">
                                    <CampaignListItem
                                        :has-multi-posting-subscription="hasMultiPostingSubscription"
                                        :campaign="campaign"
                                        @update-campaigns="getCampaigns"
                                        @duplicated-campaign="editDuplicate"
                                    />
                                </div>
                                <div
                                    v-if="group.campaigns.length === 0"
                                    class="border border-primary-20 rounded-4 py-16 px-24"
                                >
                                    <p class="text-primary-60">{{ t('multi-posting.campaigns.status.no_campaigns') }}</p>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
    <CampaignForm v-else @close="closeCampaignForm" />
</template>

<script setup>
import Loading from "../ui/Loading.vue";
import SimpleButton from "../ui/SimpleButton.vue";
import CampaignListItem from "./CampaignListItem.vue";
import {useMultiPostingStore} from '../../state/modules/multiPosting';
import CampaignFilters from "./CampaignFilters.vue";
import EmptyState from '../ui/EmptyState.vue';
import {computed, onBeforeMount, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import CampaignForm from "./CampaignForm.vue";
import bus from "../../../shared/eventbus";
import Badge from "../ui/Badge.vue";
import SvgIcon from "../ui/SvgIcon.vue";
import {useI18n} from "vue-i18n";

const multiPostingStore = useMultiPostingStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const defaultFilters = {
    search: '',
    sort: 'newest_first',
    status: [],
};

let fetching = ref(false);
let filters = ref(defaultFilters);
const openGroups = ref(['online', 'in progress']);
const showArchived = ref(false);

const props = defineProps({
    hasMultiPostingSubscription: {
        type: Boolean,
        default: false,
    }
});

const creatingCampaign = computed(() => multiPostingStore.creatingCampaign);
const campaigns = computed(() => multiPostingStore.campaigns);
const filteredCampaigns = computed(() => campaigns.value.filter(campaign => campaign.brockmeyer_old_partner === showArchived.value));
const campaignGroups = computed(() => [
    {
        name: 'online',
        title: t('multi-posting.campaigns.status.online'),
        open: openGroups.value.includes('online'),
        campaigns: onlineCampaigns.value,
        hidden: filters.value.status && ![...filters.value.status].includes('online') && filters.value.status.length > 0
    },
    {
        name: 'in progress',
        title: t('multi-posting.campaigns.status.in_progress'),
        open: openGroups.value.includes('in progress'),
        campaigns: inProgressCampaigns.value,
        hidden: filters.value.status && ![...filters.value.status].includes('in progress') && filters.value.status.length > 0
    },
    {
        name: 'draft',
        title: t('multi-posting.campaigns.status.draft'),
        open: openGroups.value.includes('draft'),
        campaigns: draftCampaigns.value,
        hidden: filters.value.status && ![...filters.value.status].includes('draft') && filters.value.status.length > 0
    },
    {
        name: 'offline',
        title: t('multi-posting.campaigns.status.offline'),
        open: openGroups.value.includes('offline'),
        campaigns: offlineCampaigns.value,
        hidden: filters.value.status && ![...filters.value.status].includes('offline') && filters.value.status.length > 0
    },
    {
        name: 'cancelled',
        title: t('multi-posting.campaigns.status.cancelled'),
        open: openGroups.value.includes('cancelled'),
        campaigns: cancelledCampaigns.value,
        hidden: filters.value.status && ![...filters.value.status].includes('cancelled') && filters.value.status.length > 0
    }
]);

const onlineCampaigns = computed(() => filteredCampaigns.value.filter(campaign => !campaign.draft_mode && campaign.status === 'online'));
const inProgressCampaigns = computed(() => filteredCampaigns.value.filter(campaign => !campaign.draft_mode && campaign.status === 'in progress'));
const draftCampaigns = computed(() => filteredCampaigns.value.filter(campaign => !!campaign.draft_mode));
const offlineCampaigns = computed(() => filteredCampaigns.value.filter(campaign => !campaign.draft_mode && campaign.status === 'offline'));
const cancelledCampaigns = computed(() => filteredCampaigns.value.filter(campaign => !campaign.draft_mode && campaign.status === 'cancelled'));
const hasFiltersApplied = computed(() => {
    return !!Object.keys(filters).some(key => {
        // check if the value is iterable
        if (Array.isArray(filters[key])) {
            return filters[key].length !== defaultFilters[key].length;
        } else {
            return filters[key] !== defaultFilters[key];
        }
    })
});

const getCampaigns = async (filter) => {
    filters.value = filter || filters.value;
    fetching.value = true;
    return await multiPostingStore.fetchCampaigns(filters.value)
        .finally(() => fetching.value = false);
}

const removeCampaignId = () => {
    const url = new URL(location.href)
    if (url.search.length === 0) return
    url.search = ''
    url.hash = ''
    router.replace({page: url})
};

const closeCampaignForm = () => {
    multiPostingStore.setCreatingCampaign(false)
    removeCampaignId()
    getCampaigns()
};

const filterArchived = (show) => {
    showArchived.value = show;
};

const editDuplicate = (campaign) => {
    getCampaigns().then(() => {
        multiPostingStore.setRequiresConfirmation(true);
        multiPostingStore.setDefaultStep(2);
        multiPostingStore.fetchLookups();
        multiPostingStore.loadCampaign(campaign);
        multiPostingStore.setCreatingCampaign(true);
        // Add campaign guid to the url
        router.push({
            query: {
                ...route.query,
                campaignId: campaign.guid,
            },
            replace: true
        });
    });
};

const toggleGroup = (groupName) => {
    if (openGroups.value.includes(groupName)) {
        openGroups.value = openGroups.value.filter(group => group !== groupName);
    } else {
        openGroups.value.push(groupName);
    }
};

watch(
    () => filters.value.status,
    (newVal) => {
        if (newVal.length === 0) {
            openGroups.value = ['online', 'in progress'];
        } else {
            openGroups.value = [...filters.value.status];
        }
    }
)

onBeforeMount(()=>{
    if (route.params.mode === 'create') {
        // most likely coming from share vacancy page - force creating campaign
        multiPostingStore.setCreatingCampaign(true)
    }

    if (!!route.params.jobId) {
        multiPostingStore.setVacancyId({
            jobId: route.params.jobId,
        });
    }
});

onMounted(() => {
    bus.addEventListener('close-campaign-form', () => {
        closeCampaignForm();
    });
    removeCampaignId();
    getCampaigns();
});
</script>